/**
 * Route Function Mapper
 *
 * Maps route paths to functionIds dynamically from backend menuList.
 * This eliminates hardcoding and ensures single source of truth.
 *
 * Convention (matches backend MenuFunction.getPath()):
 * - DASHBOARD → '/'
 * - USER → '/user'
 * - ROLE → '/role'
 * - CUSTOMER → '/customer'
 * - etc.
 */

import type { MenuFunctionItem } from '@/store/permissionStore';
import { logger } from './logger';

/**
 * Get functionId from route path using menuList from backend
 *
 * @param path - Current route path (e.g., '/user', '/role', '/')
 * @param menuList - Menu list from backend API
 * @returns functionId or null if not found
 *
 * @example
 * ```ts
 * getFunctionIdFromPath('/user', menuList) // Returns 'USER'
 * getFunctionIdFromPath('/role', menuList) // Returns 'ROLE'
 * getFunctionIdFromPath('/', menuList) // Returns 'DASHBOARD'
 * ```
 */
export const getFunctionIdFromPath = (
  path: string,
  menuList: MenuFunctionItem[],
): string | null => {
  // Normalize path: remove trailing slash except for root
  const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '');

  // Find menu item with matching path
  const menuItem = menuList.find(
    menu => menu.path && menu.path === normalizedPath,
  );

  if (menuItem?.functionId) {
    logger.debug('Found functionId from path', {
      path: normalizedPath,
      functionId: menuItem.functionId,
      nodeId: menuItem.nodeId,
    });
    return menuItem.functionId;
  }

  // Fallback: Try convention-based conversion if not found in menuList
  // This handles edge cases where menuList might not be loaded yet
  if (normalizedPath === '/') {
    logger.warn('Path "/" not found in menuList, using fallback DASHBOARD');
    return 'DASHBOARD';
  }

  // Convert path to uppercase functionId as fallback
  // /user → USER, /role → ROLE
  const pathSegment = normalizedPath.split('/').filter(Boolean)[0];
  if (pathSegment) {
    const fallbackFunctionId = pathSegment.toUpperCase();
    logger.warn('Path not found in menuList, using convention-based fallback', {
      path: normalizedPath,
      fallbackFunctionId,
    });
    return fallbackFunctionId;
  }

  logger.error(
    `Could not determine functionId from path: ${normalizedPath} (menuList: ${menuList.length} items)`,
  );
  return null;
};

/**
 * Get path from functionId using menuList from backend
 *
 * @param functionId - Function ID (e.g., 'USER', 'ROLE', 'DASHBOARD')
 * @param menuList - Menu list from backend API
 * @returns route path or null if not found
 *
 * @example
 * ```ts
 * getPathFromFunctionId('USER', menuList) // Returns '/user'
 * getPathFromFunctionId('ROLE', menuList) // Returns '/role'
 * getPathFromFunctionId('DASHBOARD', menuList) // Returns '/'
 * ```
 */
export const getPathFromFunctionId = (
  functionId: string,
  menuList: MenuFunctionItem[],
): string | null => {
  const upperFunctionId = functionId.toUpperCase();

  // Find menu item with matching functionId
  const menuItem = menuList.find(
    menu =>
      menu.functionId && menu.functionId.toUpperCase() === upperFunctionId,
  );

  if (menuItem?.path) {
    return menuItem.path;
  }

  // Fallback: Use convention-based conversion
  if (upperFunctionId === 'DASHBOARD') {
    return '/';
  }

  return `/${functionId.toLowerCase()}`;
};

/**
 * Check if a path is accessible based on allowed functions
 *
 * @param path - Current route path
 * @param allowedFunctions - Array of allowed functionIds
 * @param menuList - Menu list from backend API
 * @returns true if user has permission, false otherwise
 */
export const isPathAccessible = (
  path: string,
  allowedFunctions: string[],
  menuList: MenuFunctionItem[],
): boolean => {
  const functionId = getFunctionIdFromPath(path, menuList);

  if (!functionId) {
    logger.warn('No functionId found for path, denying access', { path });
    return false;
  }

  const hasAccess = allowedFunctions.some(
    f => f.toUpperCase() === functionId.toUpperCase(),
  );

  logger.debug('Path accessibility check', {
    path,
    functionId,
    hasAccess,
    allowedFunctions,
  });

  return hasAccess;
};
